import { Component, OnInit, ViewContainerRef, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NavigationStart, Router, RouterEvent } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { ModalService } from './shared/modules/modal/modal.service';
import { ModalCreationService } from './shared/modules/modal/modal-creation.service';
import { AppConfig } from './shared/services/app-config';
import { TinyTextEditor, WalkmeConstants } from './shared/constants';
import { SessionService } from './shared/services';
import { IdleService } from './shared/services/idle.service';
import { LaunchDarklyService } from './shared/services/launch-darkly.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  @ViewChild('modalParentElement', { read: ViewContainerRef }) modalParentElement;

  constructor(
    private _modalService: ModalService,
    private _modalCreationService: ModalCreationService,
    private _launchDarklyService: LaunchDarklyService,
    private _router: Router,
    private _translate: TranslateService,
    private _config: AppConfig,
    private _sessionService: SessionService,
    private _idleService: IdleService
  ) {
    // Set up UI multilanguge
    this._translate.setDefaultLang('en-US');
    // TODO: remove this later, as this value will be user-chosen in future.
    this._translate.use('en-US');
    this._idleService.init();
  }

  ngOnInit(): void {
    this._modalCreationService.setParentElementForModal(this.modalParentElement);

    this._modalService.openModalEvent$.subscribe((modalData) => {
      this._modalCreationService.createModal(modalData);
    });

    this._modalService.closeModalEvent$.subscribe((closeAll) => {
      this._modalCreationService.closeModal(closeAll);
    });

    this.addWalkmeScript();
    this._launchDarklyService.client.waitUntilReady().then(() =>{
      const isRebranding = this._launchDarklyService.client.variation('tp-rebranding', false);
      if (isRebranding) {
        this.replaceFavIconWithExacteraIcon();
      }

      // JPD-177: restore in-editor spellcheck via the self-hosted
      // LanguageTool server. Flag OFF is the kill-switch — the editor INIT
      // is never touched and behaves exactly as it does today.
      const languageToolEnabled = this._launchDarklyService.client.variation('editor-languagetool-spellcheck', false);
      if (languageToolEnabled) {
        const envShort = this._config.getEnvironmentShort();
        if (!envShort) {
          // Fall back to dev rather than emit a malformed host, but surface it: a
          // missing env short means config didn't load, and silently checking dev
          // from another env would be a confusing failure mode to debug.
          console.warn('xbsEnvironmentShort is not set; defaulting LanguageTool URL to dev.');
        }

        // URL derivation lives in TinyTextEditor.buildLanguageToolUrl (pure, unit-tested):
        // an empty-string/undefined short falls back to dev rather than emitting a
        // malformed `languagetool.xbs.com`.
        TinyTextEditor.enableLanguageTool(
          TinyTextEditor.buildLanguageToolUrl(envShort)
        );
      }
    });

    this._idleService.subscribe();

    this._router.events
      .pipe(
        filter((event: RouterEvent) => event instanceof NavigationStart),
        takeUntil(new Subject()),
      )
      .subscribe(() => {
        return this._launchDarklyService.client.waitUntilReady().then(
          () => {
            const redirectToNewUI = this._launchDarklyService.client.variation('force-redirect-to-new-ui', false);

            if (redirectToNewUI && !window.location.pathname.includes('/legacy/master-file')) {
              this.goToNewExperience();
            }
          }
        );
      });
  }

  ngOnDestroy(): void {
    this._launchDarklyService.client.close();
  }
  replaceFavIconWithExacteraIcon(): void{
    const faviconLink = document.querySelector('link[rel*=\'icon\']');
    if (faviconLink) {
      (faviconLink as HTMLLinkElement).href = 'favicon.png';
    }
  }
  addWalkmeScript(): void {
    let node = document.createElement('script');
    node.type = 'text/javascript';
    node.innerHTML = ['develop', 'staging'].includes(this._config.getEnvironment()) ? WalkmeConstants.WALKME_SCRIPT_TEST : WalkmeConstants.WALKME_SCRIPT_PROD;
    document.getElementsByTagName('head')[0].appendChild(node);
    this._sessionService.setUserIdForWalkMe();
  }

  goToNewExperience(): void {
    const envShort = this._config.getEnvironmentShort();
    const exacteraDomainUrl = this._config.getExacteraDomainUrl();
    const hostname = window.location.hostname;
    
    const redirectUrl =
      envShort === 'prod' ?
        `https://tp.${exacteraDomainUrl}` :
        `https://tp.${envShort}.${exacteraDomainUrl}`;

    if (hostname.includes('xbscompliance')) {
      window.location.replace(redirectUrl);
    }
    window.location.href = '/newexperience/';
  }
}
